import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { GraduationCap, Loader2, Lock, Mail, Eye, EyeOff, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EntrenadorLoginPage() {
  const { academiaId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [academia, setAcademia] = useState(null);
  const [error, setError] = useState(null);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    loadAcademia();
  }, [academiaId]);

  const loadAcademia = async () => {
    try {
      const res = await axios.get(`${API}/academia/${academiaId}/info-publica`);
      setAcademia(res.data);
    } catch (err) {
      setError('Academia no encontrada');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Ingresa email y contraseña');
      return;
    }

    setSubmitting(true);
    try {
      // Login normal
      await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      
      // Verificar que es entrenador de esta academia
      const meRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
      const user = meRes.data;
      
      // Verificar si tiene acceso a esta academia como entrenador
      const entrenadorRes = await axios.get(`${API}/academia/${academiaId}/mi-acceso`, { withCredentials: true });
      
      if (entrenadorRes.data.tiene_acceso) {
        // Redirigir al dashboard de entrenador
        navigate(`/academia/${academiaId}/entrenador`);
      } else {
        toast.error('No tienes acceso como entrenador a esta academia');
        await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Credenciales incorrectas');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900/30 to-slate-900 flex items-center justify-center p-4">
        <Card className="p-8 bg-slate-800 border-slate-700 text-center">
          <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-300">Cargando...</p>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-red-900/20 to-slate-900 flex items-center justify-center p-4">
        <Card className="p-8 bg-slate-800 border-slate-700 text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Error</h2>
          <p className="text-slate-400 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} variant="outline" className="border-slate-600 text-slate-300">
            Ir al inicio
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900/30 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <Card className="p-8 bg-slate-800 border-slate-700">
          {/* Header con logo de academia */}
          <div className="text-center mb-6">
            {academia?.logo ? (
              <img src={academia.logo} alt={academia.nombre} className="w-20 h-20 rounded-xl mx-auto mb-4 object-cover" />
            ) : (
              <div className="w-20 h-20 bg-emerald-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="w-10 h-10 text-white" />
              </div>
            )}
            <h1 className="text-2xl font-bold text-white mb-1">
              {academia?.nombre}
            </h1>
            <p className="text-slate-400 text-sm">
              Acceso para Entrenadores
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label className="text-slate-300">Correo electrónico</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  className="pl-10 bg-slate-700 border-slate-600 text-white"
                  required
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 pr-10 bg-slate-700 border-slate-600 text-white"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Ingresando...
                </>
              ) : (
                'Ingresar'
              )}
            </Button>
          </form>

          <p className="text-center text-slate-500 text-xs mt-6">
            Solo entrenadores autorizados pueden acceder.
          </p>
        </Card>
      </div>
    </div>
  );
}
